/* eslint-disable react/prop-types */
import React from 'react';
import { I18nextProvider } from 'react-i18next';
import { createFabricApi } from '@immediate_media/fabric-api-clients';

import { CurrentUserProvider, useCurrentUser, useLoggedIn } from '@immediate_media/auth-utils';
import { SiteHeader } from '@immediate_media/components';
import { LoggedInContext, AutocompleteContext } from '@immediate_media/component-config';
import { SiteContext, SettingsContext } from '@immediate_media/pocket-config';
import { useAccountSettings } from '@immediate_media/pocket-hooks';
import geti18NextConfig from '../../geti18NextConfig';

export const getAutoComplete = (sitekey, isAutocomplete, env) => {
  if (!isAutocomplete) return {};

  const fabricApi = createFabricApi({
    env,
    siteKey: sitekey,
  });

  const autocompleteApi = async (input) => {
    const filters = {
      search: input,
      limit: 5,
      views: 'core',
      sitekey,
    };

    const results = await fabricApi.search.searchv5.filter(filters);

    const response = results['hydra:member'].map((result) => {
      const { canonicalPath, title, type } = result;
      const indexOf = canonicalPath.indexOf('/');
      const url = canonicalPath.slice(indexOf);
      const id = result['@id'].split('/').pop();
      const sanitisedTitle = title?.replace(/<(\/|)em>/gi, '') || '';

      return {
        title: sanitisedTitle,
        type,
        url,
        id,
      };
    });

    return {
      results: response,
      input,
    };
  };

  return autocompleteApi;
};

// useAccountSettings relies on SiteContext.
// We need to make sure that the context is initialised
// before we attempt to set the account settings.
// Using a function here allows us to do that
const TempLayout = ({ defaultLayoutProps }) => {
  const { showAccountNav } = defaultLayoutProps;
  const accountSettings = useAccountSettings();
  const [, isLoadingCurrentUser] = useCurrentUser();
  const shouldShowAccountNav = showAccountNav && !isLoadingCurrentUser;

  return (
    <SiteHeader
      {...defaultLayoutProps}
      {...(shouldShowAccountNav ? accountSettings : {})}
      fixedPositioning
    />
  );
};

const Header = (props) => {
  const isLoggedIn = useLoggedIn();

  const {
    siteKey,
    search,
    env,
    settings,
  } = props;

  const i18NextConfig = geti18NextConfig(siteKey);

  // Remove unnecessary values for layout
  const layoutProps = Object.keys(props)
    .filter((key) => key !== 'settings' && key !== 'env' && key !== 'siteKey')
    .reduce((acc, key) => {
      acc[key] = props[key];
      return acc;
    }, {});

  const { isAutocomplete } = search || {};

  return (
    <CurrentUserProvider>
      <SettingsContext.Provider
        value={settings}
      >
        <SiteContext.Provider
          value={{ siteKey, env }}
        >
          <LoggedInContext.Provider
            value={isLoggedIn}
          >
            <AutocompleteContext.Provider
              value={getAutoComplete(siteKey, isAutocomplete, env)}
            >
              <I18nextProvider value={i18NextConfig}>
                <TempLayout
                  defaultLayoutProps={layoutProps}
                />
              </I18nextProvider>
            </AutocompleteContext.Provider>
          </LoggedInContext.Provider>
        </SiteContext.Provider>
      </SettingsContext.Provider>
    </CurrentUserProvider>
  );
};

export default Header;
